import type { ComponentType, SyntheticEvent } from "react";
import HealthRecordCardTypeConsultation from "@/features/MyHealth/components/HealthRecordCardTypeConsultation";
import {
  abbyDetails,
  captionDetails,
  consultationDetails,
  diagnosisDetails,
  prescriptionDetails,
  assistanceDetails,
  pathologyDetails,
  pathologyOrderId,
} from "./details";
import HealthRecordCardTypeAbby from "../HealthRecordCardTypeAbby";
import {
  HealthRecordEntryType,
  type HealthRecordEvent,
} from "../../../../services/core-api-adapter";
import HealthRecordCardTypeMedicalDiagnosis from "../HealthRecordCardTypeMedicalDiagnosis";
import HealthRecordCardTypeWhatsAppNurseChat from "../HealthRecordCardTypeWhatsAppNurseChat";
import HealthRecordCardTypeTelemedicineConsult from "../HealthRecordCardTypeTelemedicineConsult";
import HealthRecordCardTypeMedicineDispensed from "../HealthRecordCardTypeMedicineDispensed";
import HealthRecordCardTypeWhatsAppCounsellorChat from "../HealthRecordCardTypeWhatsAppCounsellorChat";
import { useTranslation } from "react-i18next";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Stack,
  Typography,
} from "@mui/material";
import Panel from "../../../Common/components/Panel";
import { convertPxToRem } from "../../../../utils";
import theme from "../../../../theme";
import IconLoader from "../../../Common/components/IconLoader";
import HealthRecordCardTypeFaceScan from "../HealthRecordCardTypeFaceScan";
import HealthRecordCardTypeMedicalAssistance from "../HealthRecordCardTypeMedicalAssistance";
import HealthRecordCardTypePathologyTest from "../HealthRecordCardTypePathologyTest";
import { useNavigate } from "react-router-dom";
import {
  FeatureToggleIdentifier,
  isFeatureEnabled,
} from "@/services/feature-toggle-adapter";

export interface DefaultHealthRecordCardTypeProps {
  hasDetails: boolean;
}

interface HealthRecordCardProps {
  data: HealthRecordEvent;
  onAccordionOpen: (type: string) => void;
}

interface HealthRecordCardTypeMapType {
  [key: string]: {
    component: ComponentType<{
      data: HealthRecordEvent;
      hasDetails: boolean;
    }> | null;
    resolveDetails?: (details: unknown) => Record<string, unknown> | null;
    color?: string;
    analyticsTrackingTypeName?: string;
  };
}

const HealthRecordCardTypeMap: HealthRecordCardTypeMapType = {
  UNKNOWN: {
    component: null,
  },
  LIFESTYLE_QUESTIONNAIRE: {
    component: null,
    color: theme.palette.primary.main,
  },
  MENTAL_WELLBEING_QUESTIONNAIRE: {
    component: null,
    color: theme.palette.primary.main,
  },
  HEALTH_HISTORY_QUESTIONNAIRE: {
    component: null,
    color: theme.palette.primary.main,
  },
  MOBILE_HEALTH_DATA: {
    component: null,
    color: theme.palette.primary.main,
  },
  ABBY: {
    resolveDetails: abbyDetails,
    component: HealthRecordCardTypeAbby,
    color: theme.palette.primary.main,
    analyticsTrackingTypeName: "Abby",
  },
  FACE_SCAN: {
    resolveDetails: captionDetails,
    component: HealthRecordCardTypeFaceScan,
    color: theme.palette.primary.main,
    analyticsTrackingTypeName: "BINAH",
  },
  MEDICAL_DIAGNOSIS: {
    resolveDetails: diagnosisDetails,
    component: HealthRecordCardTypeMedicalDiagnosis,
    color: theme.palette.secondary[700],
    analyticsTrackingTypeName: "ICD10",
  },
  WHATSAPP_NURSE_CHAT: {
    resolveDetails: captionDetails,
    component: HealthRecordCardTypeWhatsAppNurseChat,
    color: "accent1.900",
    analyticsTrackingTypeName: "Nurse Consult",
  },
  TELEMEDICINE_CONSULT: {
    resolveDetails: captionDetails,
    component: HealthRecordCardTypeTelemedicineConsult,
    color: "accent1.900",
    analyticsTrackingTypeName: "Nurse Consult",
  },
  WHATSAPP_COUNSELLOR_CHAT: {
    resolveDetails: captionDetails,
    component: HealthRecordCardTypeWhatsAppCounsellorChat,
    color: "accent1.900",
    analyticsTrackingTypeName: "Nurse Consult",
  },
  MEDICINE_DISPENSED: {
    resolveDetails: prescriptionDetails,
    component: HealthRecordCardTypeMedicineDispensed,
    color: "accent1.500",
    analyticsTrackingTypeName: "NAPPI",
  },
  MEDICAL_ASSISTANCE: {
    resolveDetails: assistanceDetails,
    component: HealthRecordCardTypeMedicalAssistance,
    color: "accent1.900",
    analyticsTrackingTypeName: "Medical assistance",
  },
  CONSULTATION: {
    resolveDetails: consultationDetails,
    component: HealthRecordCardTypeConsultation,
    color: theme.palette.primary.main,
    analyticsTrackingTypeName: "Intercare",
  },
  PATHOLOGY: {
    resolveDetails: pathologyDetails,
    component: HealthRecordCardTypePathologyTest,
    color: "accent1.900",
    analyticsTrackingTypeName: "Pathology",
  },
};

export const healthRecordCardLabelStyle = {
  fontSize: convertPxToRem(10),
  color: theme.palette.neutral[500],
  fontWeight: 600,
};

function HealthRecordCardPreview({
  dateTime,
  name,
  description,
  type,
}: {
  dateTime: string;
  name: string;
  description: string;
  type: HealthRecordEntryType;
}) {
  const { t } = useTranslation();

  function getCardDataBasedOnRecordEntryType(
    dataSourceType: HealthRecordEntryType
  ): { sourceValue: string; description: string } {
    if (dataSourceType === HealthRecordEntryType.FACE_SCAN) {
      return {
        sourceValue: t(`HealthRecordCardType.${dataSourceType}.sourceValue`),
        description: t(`HealthRecordCardType.${dataSourceType}.description`),
      };
    }

    if (dataSourceType === HealthRecordEntryType.ABBY) {
      return {
        sourceValue: t(`HealthRecordCardType.${dataSourceType}.sourceValue`),
        description: t(`HealthRecordCardType.${dataSourceType}.description`),
      };
    }

    if (dataSourceType === HealthRecordEntryType.SWEETLIFE_BOT) {
      return {
        sourceValue: t(`HealthRecordCardType.${dataSourceType}.sourceValue`),
        description: t(
          `HealthRecordCardType.${dataSourceType}.description.${description}` as any
        ),
      };
    }

    if (dataSourceType === HealthRecordEntryType.PATHOLOGY) {
      return {
        sourceValue: t(`HealthRecordCardType.${dataSourceType}.sourceValue`),
        description: t(`HealthRecordCardType.${dataSourceType}.description`),
      };
    }

    return {
      sourceValue: name,
      description: description,
    };
  }

  return (
    <Stack
      spacing={1}
      sx={{ width: "100%", minWidth: 0, overflowWrap: "anywhere" }}
    >
      <Stack direction="row" sx={{ flexWrap: "wrap", gap: 2 }}>
        <Stack sx={{ flex: "1 1 140px", minWidth: 0 }} spacing={1}>
          <Typography variant="subtitle2">
            {t(`HealthRecordCardType.${type}.source` as any, {
              defaultValue: t("HealthRecordCardType.Default.sourceLabel"),
            })}
          </Typography>
          <Typography variant="h4">
            {getCardDataBasedOnRecordEntryType(type).sourceValue}
          </Typography>
        </Stack>
        <Stack sx={{ flex: "0 0 auto" }} spacing={1}>
          <Typography variant="subtitle2">
            {t("HealthRecordCardType.Default.dateLabel")}
          </Typography>
          <Typography variant="h4">
            {new Date(dateTime)?.toISOString()?.split("T")[0]}
          </Typography>
        </Stack>
      </Stack>
      <Stack spacing={1}>
        <Typography variant="subtitle2">
          {t("HealthRecordCardType.Default.descriptionLabel")}
        </Typography>
        <Typography variant="h4">
          {getCardDataBasedOnRecordEntryType(type).description}
        </Typography>
      </Stack>
    </Stack>
  );
}

export default function HealthRecordCard({
  data,
  onAccordionOpen,
}: HealthRecordCardProps) {
  const isTrendedVitalsEnabled = isFeatureEnabled(
    FeatureToggleIdentifier.ENABLE_TRENDED_VITALS
  );
  const { t } = useTranslation();
  const healthRecordCardType =
    HealthRecordCardTypeMap[data.type] || HealthRecordCardTypeMap.UNKNOWN;
  const details = healthRecordCardType.resolveDetails?.(data.details);
  const DetailComponent = healthRecordCardType.component;
  const hasDetails = Boolean(details && DetailComponent);
  const navigate = useNavigate();
  const orderId =
    data.type === HealthRecordEntryType.PATHOLOGY
      ? pathologyOrderId(data.details)
      : undefined;
  const preview = (
    <HealthRecordCardPreview
      dateTime={data.dateTime}
      description={data.description}
      name={data.name}
      type={data.type}
    />
  );

  function onAccordionChange(_: SyntheticEvent, expanded: boolean) {
    if (
      expanded &&
      hasDetails &&
      healthRecordCardType.analyticsTrackingTypeName
    ) {
      return onAccordionOpen(healthRecordCardType.analyticsTrackingTypeName);
    }
  }

  const shouldShowFooter =
    data.type !== HealthRecordEntryType.MEDICAL_ASSISTANCE;

  return (
    <Panel accentColour={healthRecordCardType.color} padding={0}>
      <Stack
        sx={{
          overflow: "hidden",
        }}
      >
        {hasDetails ? (
          <Accordion
            disableGutters={true}
            onChange={onAccordionChange}
            sx={{
              ".MuiButtonBase-root": {
                alignItems: "flex-start",
              },
              ".MuiAccordionSummary-expandIconWrapper": {
                mt: 2,
                mr: 2,
                position: isTrendedVitalsEnabled ? "absolute" : "",
                right: 0,
              },
              ".MuiAccordionSummary-root": {
                p: 0,
              },
              ".MuiAccordionSummary-content": {
                minWidth: 0,
                m: 0,
                p: 2,
              },
            }}
          >
            <AccordionSummary
              expandIcon={<IconLoader icon="ChevronDownIcon" />}
            >
              {preview}
            </AccordionSummary>
            {hasDetails && (
              <AccordionDetails sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
                <Stack spacing={2} sx={{ minWidth: 0 }}>
                  {DetailComponent && (
                    <DetailComponent
                      data={{ ...data, details }}
                      hasDetails={true}
                    />
                  )}
                  {shouldShowFooter && (
                    <Stack direction={"row"}>
                      <Stack sx={{ width: "50%" }} spacing={1}>
                        <Typography variant="subtitle2">
                          {t("HealthRecordCardType.Default.dateLabel")}:
                        </Typography>
                        <Typography variant="body2">
                          {
                            new Date(data.dateTime)
                              ?.toISOString()
                              ?.split("T")[0]
                          }
                        </Typography>
                      </Stack>
                      <Stack spacing={1}>
                        <Typography variant="subtitle2">
                          {t("HealthRecordCardType.Default.timeLabel")}:
                        </Typography>
                        <Typography variant="body2">
                          {new Date(data.dateTime).toLocaleTimeString()}
                        </Typography>
                      </Stack>
                    </Stack>
                  )}
                </Stack>
              </AccordionDetails>
            )}
          </Accordion>
        ) : (
          <Stack p={2}>{preview}</Stack>
        )}
        {orderId && (
          <Stack p={2}>
            <Button
              variant="contained"
              fullWidth
              size="small"
              onClick={() => {
                void navigate(
                  `/home/get-medical-assistance/pathology?orderId=${encodeURIComponent(orderId)}`
                );
              }}
            >
              {t("HealthRecordCardType.PATHOLOGY.buttonText")}
            </Button>
          </Stack>
        )}
      </Stack>
    </Panel>
  );
}
